/**
 * Home (REDESIGN.md 7.2): crest, hero, tagline, two buttons and a link.
 * Setup lives in small sheets; settings behind the gear, help behind "?".
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAssets } from '../assets';
import { DEFAULT_OPPONENTS, HOLE_CHOICES, LEVEL_LABEL, MAX_OPPONENTS, TAGLINE, holeCounter } from '../brand';
import { Crest, Pic } from '../components/Art';
import { HowToPlay } from '../components/HowToPlay';
import { IconGear, IconMinus, IconMute, IconOffline, IconPlay, IconPlus, IconSound } from '../components/Icons';
import { GetApp } from '../components/GetApp';
import { Sheet } from '../components/Sheet';
import { useInstall } from '../lib/install';
import { useToast } from '../components/Toasts';
import { clearSolo, createSolo, loadSolo, normalizeSetup } from '../game/localGame';
import type { SoloSetup } from '../game/localGame';
import { createRoom, getRoomInfo, normalizeRoomCode } from '../net/roomClient';
import { reduceMotionSetting, setReduceMotionSetting } from '../lib/motion';
import { navigate } from '../lib/router';
import { isMuted, onMuteChange, setMuted, unlockAudio } from '../lib/sound';
import { KEYS, load, save } from '../lib/storage';
import { BOT_LEVELS, cleanName } from '../../worker/protocol';
import { OPEN_PLAY, tutorialDone } from '../tutorial/keys';
import '../tutorial/tutorial.css';

type Panel = null | 'solo' | 'friends' | 'join' | 'settings' | 'confirm';

function Toggle({ on, label, onClick, icon }: { on: boolean; label: string; onClick: () => void; icon?: ReactNode }) {
  return (
    <button type="button" className="setting-row" role="switch" aria-checked={on} onClick={onClick}>
      {icon && <span className="setting-ic">{icon}</span>}
      <span className="setting-label">{label}</span>
      <span className={`switch${on ? ' on' : ''}`} aria-hidden="true" />
    </button>
  );
}

export function Home() {
  const assets = useAssets();
  const toast = useToast();
  const [name, setName] = useState<string>(() => load(KEYS.name, ''));
  const [panel, setPanel] = useState<Panel>(null);
  const [help, setHelp] = useState(false);
  const [getApp, setGetApp] = useState(false);
  const { installed } = useInstall();
  const [setup, setSetup] = useState<SoloSetup>(() => normalizeSetup(load<unknown>(KEYS.soloSetup, { opponents: DEFAULT_OPPONENTS })));
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejoin, setRejoin] = useState<string | null>(null);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [muted, setMutedState] = useState(isMuted());
  const [reduce, setReduce] = useState(reduceMotionSetting());
  const [skipTravel, setSkipTravel] = useState<boolean>(() => load(KEYS.skipTravel, false));
  useEffect(() => onMuteChange(setMutedState), []);
  // The tutorial's finish button asks for the Play sheet (docs/HINTS-LAYOUT-TUTORIAL.md B8).
  useEffect(() => {
    try {
      if (sessionStorage.getItem(OPEN_PLAY) === '1') {
        sessionStorage.removeItem(OPEN_PLAY);
        setPanel('solo');
      }
    } catch {
      /* storage blocked: just stay on home */
    }
  }, []);
  const saved = loadSolo();
  const firstTimer = !load(KEYS.holeDone, false) && !tutorialDone();
  const learn = () => {
    setPanel(null);
    navigate({ name: 'learn' });
  };
  const resumable = saved && saved.state.phase !== 'gameOver' ? saved : null;

  useEffect(() => {
    const on = () => setOnline(navigator.onLine);
    window.addEventListener('online', on);
    window.addEventListener('offline', on);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', on);
    };
  }, []);

  useEffect(() => {
    const last = load<{ code: string } | null>(KEYS.lastRoom, null);
    if (!last?.code) return;
    let live = true;
    getRoomInfo(last.code)
      .then((info) => {
        if (live && info?.seated) setRejoin(info.code);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const keepName = (v: string) => {
    setName(v);
    save(KEYS.name, v);
  };
  const cleaned = cleanName(name);

  const startSolo = () => {
    unlockAudio();
    save(KEYS.soloSetup, setup);
    clearSolo();
    save(KEYS.solo, createSolo(setup, cleaned ?? 'You'));
    navigate({ name: 'solo' });
  };

  const requireName = (): string | null => {
    if (cleaned) return cleaned;
    toast('Add a name first.', 'error');
    document.getElementById('name-field')?.focus();
    return null;
  };

  const create = async () => {
    if (!requireName()) return;
    setBusy(true);
    try {
      const c = await createRoom();
      navigate({ name: 'room', code: c });
    } catch {
      toast('Could not create a game. Check your connection and try again.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const join = async () => {
    if (!requireName()) return;
    const c = normalizeRoomCode(code);
    if (!c) {
      setCodeError('Game codes are 4 letters, like ABCD.');
      return;
    }
    setBusy(true);
    try {
      const info = await getRoomInfo(c);
      if (!info) setCodeError(`No game called ${c}. Check the code.`);
      else navigate({ name: 'room', code: c });
    } catch {
      toast('Could not reach the server. Check your connection.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const friends = () => {
    if (!online) {
      toast('Online play needs a connection.');
      return;
    }
    setPanel('friends');
  };

  const nameField = (
    <label className="field">
      <span>Your name</span>
      <input id="name-field" value={name} maxLength={20} autoComplete="nickname" placeholder="Sam" onChange={(e) => keepName(e.target.value)} />
    </label>
  );

  return (
    <main className="home">
      <div className="home-corner">
        <button className="icon-btn" onClick={() => setHelp(true)} aria-label="Help">
          <span className="help-glyph">?</span>
        </button>
        <button className="icon-btn" onClick={() => setPanel('settings')} aria-label="Settings">
          <IconGear />
        </button>
      </div>

      <section className="hero">
        <Pic
          img={assets.hero}
          set={assets.heroSet}
          portrait={assets.heroPortrait}
          sizes="100vw"
          eager
          className="hero-img"
          alt="Tibbs tees off across the ancient world"
          fallback={<div className="hero-fallback" aria-hidden="true" />}
        />
        <div className="hero-crest">
          <Crest width={220} />
        </div>
      </section>

      <div className="home-body">
        <h1 className="sr-only">Ancient Nine</h1>
        <p className="tagline">{TAGLINE}</p>

        <div className="home-actions">
          {resumable ? (
            <>
              <button className="btn btn-primary btn-big home-btn" onClick={() => navigate({ name: 'solo' })} data-autofocus>
                <IconPlay /> Resume
                <span className="resume-hole num">{holeCounter(resumable.state.hole, resumable.state.totalHoles)}</span>
              </button>
            </>
          ) : (
            <button className="btn btn-primary btn-big home-btn" onClick={() => setPanel('solo')}>
              Play
            </button>
          )}
          {rejoin && (
            <button className="btn btn-quiet home-btn" onClick={() => navigate({ name: 'room', code: rejoin })}>
              Rejoin <span className="num">{rejoin}</span>
            </button>
          )}
          <div className={resumable ? 'home-pair' : 'home-single'}>
            {resumable && (
              <button className="btn btn-quiet home-btn" onClick={() => setPanel('solo')}>
                New game
              </button>
            )}
            <button className={`btn btn-quiet home-btn${online ? '' : ' offline'}`} onClick={friends} aria-label={online ? 'Play with friends' : 'Play with friends (offline)'}>
              {!online && <IconOffline />} {resumable ? 'With friends' : 'Play with friends'}
            </button>
          </div>
        </div>
        <div className="home-links">
          <button className="link-btn join-link" onClick={() => (online ? setPanel('join') : toast('Online play needs a connection.'))}>
            Join a game
          </button>
          <span className="home-links-dot" aria-hidden="true">
            ·
          </span>
          <button className="link-btn join-link" onClick={learn}>
            Learn to play
          </button>
          {!installed && (
            <>
              <span className="home-links-dot" aria-hidden="true">
                ·
              </span>
              <button className="link-btn join-link" onClick={() => setGetApp(true)}>
                Get the app
              </button>
            </>
          )}
        </div>
      </div>

      <Sheet open={panel === 'solo'} onClose={() => setPanel(null)} title="Play" labelledBy="solo-title" className="setup-sheet">
        <div className="setup-row">
          <span className="setup-label" id="opp-label">
            Opponents
          </span>
          <div className="stepper" role="group" aria-labelledby="opp-label">
            <button
              type="button"
              className="icon-btn"
              aria-label="Fewer opponents"
              disabled={setup.opponents <= 1}
              onClick={() => setSetup((s) => ({ ...s, opponents: Math.max(1, s.opponents - 1) }))}
            >
              <IconMinus />
            </button>
            <span className="stepper-val num" aria-live="polite">
              {setup.opponents}
            </span>
            <button
              type="button"
              className="icon-btn"
              aria-label="More opponents"
              disabled={setup.opponents >= MAX_OPPONENTS}
              onClick={() => setSetup((s) => ({ ...s, opponents: Math.min(MAX_OPPONENTS, s.opponents + 1) }))}
            >
              <IconPlus />
            </button>
          </div>
        </div>
        <div className="setup-row">
          <span className="setup-label" id="lvl-label">
            Level
          </span>
          <div className="seg" role="radiogroup" aria-labelledby="lvl-label">
            {BOT_LEVELS.map((l) => (
              <button
                key={l}
                type="button"
                role="radio"
                aria-checked={setup.level === l}
                className={`seg-btn${setup.level === l ? ' on' : ''}`}
                onClick={() => setSetup((s) => ({ ...s, level: l }))}
              >
                {LEVEL_LABEL[l]}
              </button>
            ))}
          </div>
        </div>
        <div className="setup-row">
          <span className="setup-label" id="holes-label">
            Holes
          </span>
          <div className="seg" role="radiogroup" aria-labelledby="holes-label">
            {HOLE_CHOICES.map((h) => (
              <button
                key={h}
                type="button"
                role="radio"
                aria-checked={setup.holes === h}
                className={`seg-btn${setup.holes === h ? ' on' : ''}`}
                onClick={() => setSetup((s) => ({ ...s, holes: h }))}
              >
                {h}
              </button>
            ))}
          </div>
        </div>
        <button className="btn btn-gold btn-big wide-btn" onClick={() => (resumable ? setPanel('confirm') : startSolo())} data-autofocus>
          Tee off
        </button>
        {firstTimer && (
          <button className="btn btn-quiet wide-btn learn-first" onClick={learn}>
            Learn to play first
          </button>
        )}
      </Sheet>

      <Sheet open={panel === 'confirm'} onClose={() => setPanel('solo')} title="Start over?" labelledBy="confirm-title">
        <p className="confirm-text">Your current game ends.</p>
        <div className="sheet-actions">
          <button className="btn btn-ink" onClick={() => setPanel('solo')} data-autofocus>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={startSolo}>
            Start over
          </button>
        </div>
      </Sheet>

      <Sheet open={panel === 'friends'} onClose={() => setPanel(null)} title="Play with friends" labelledBy="friends-title" className="online-sheet">
        {nameField}
        <button className="btn btn-gold btn-big wide-btn" onClick={create} disabled={busy}>
          Create a game
        </button>
      </Sheet>

      <Sheet open={panel === 'join'} onClose={() => setPanel(null)} title="Join a game" labelledBy="join-title" className="online-sheet">
        {nameField}
        <form
          className="join-row"
          onSubmit={(e) => {
            e.preventDefault();
            void join();
          }}
        >
          <input
            className="code-input num"
            value={code}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            placeholder="ABCD"
            aria-label="Game code"
            aria-invalid={!!codeError}
            aria-describedby={codeError ? 'code-err' : undefined}
            maxLength={4}
            onChange={(e) => {
              setCodeError(null);
              setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4));
            }}
          />
          <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 4}>
            Join
          </button>
        </form>
        {codeError && (
          <p className="field-error" id="code-err" role="alert">
            {codeError}
          </p>
        )}
      </Sheet>

      <Sheet open={panel === 'settings'} onClose={() => setPanel(null)} title="Settings" labelledBy="settings-title">
        <div className="settings">
          <Toggle on={!muted} label="Sound" icon={muted ? <IconMute /> : <IconSound />} onClick={() => setMuted(!muted)} />
          <Toggle
            on={reduce}
            label="Reduce motion"
            onClick={() => {
              setReduceMotionSetting(!reduce);
              setReduce(!reduce);
            }}
          />
          <Toggle
            on={skipTravel}
            label="Skip travel scenes"
            onClick={() => {
              save(KEYS.skipTravel, !skipTravel);
              setSkipTravel(!skipTravel);
            }}
          />
        </div>
      </Sheet>

      <GetApp open={getApp} onClose={() => setGetApp(false)} />
      <HowToPlay open={help} onClose={() => setHelp(false)} />
    </main>
  );
}
